import * as React from "react"
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react"

import logger from '@/lib/logging';
import { applyShellCanvasToDocument } from '@/lib/theme-shell-sync';

export type Theme = "dark" | "light" | "system" | "high-contrast" | "glass"

/** Order used by header theme cycle control (matches Settings theme picker). */
export const BASIC_THEME_CYCLE: readonly Theme[] = [
  "light",
  "dark",
  "system",
  "high-contrast",
  "glass",
]

export const BASIC_THEME_LABELS: Record<Theme, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
  "high-contrast": "High Contrast",
  glass: "Glass",
}

export function getNextBasicTheme(current: Theme): Theme {
  const idx = BASIC_THEME_CYCLE.indexOf(current)
  const safeIdx = idx === -1 ? 0 : idx
  return BASIC_THEME_CYCLE[(safeIdx + 1) % BASIC_THEME_CYCLE.length]
}

/** @deprecated stored value — migrated to `glass` on read */
const LEGACY_GLASS_THEME = "transparent" as const

type StoredTheme = Theme | typeof LEGACY_GLASS_THEME

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  attribute?: string
  enableSystem?: boolean
  themes?: string[]
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

export const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

/** Class tokens mutually exclusive on `<html>` for basic appearance (matches Theme matrix + seed script). */
const DOM_BASIC_THEME_CLASSES: Theme[] = ["light", "dark", "high-contrast", "glass"]

/** Public funnel marker. While present, appearance stays light even if Settings stored dark, glass, or high contrast. */
export const SELF_ASSESSMENT_PAGE_CLASS = "self-assessment-page"

export const BASIC_THEME_STORAGE_KEY = "vite-ui-theme"

function normalizeStoredTheme(raw: StoredTheme | null): Theme | null {
  if (raw === LEGACY_GLASS_THEME) return "glass"
  if (
    raw === "light" ||
    raw === "dark" ||
    raw === "system" ||
    raw === "high-contrast" ||
    raw === "glass"
  ) {
    return raw
  }
  return null
}

function readStoredBasicTheme(storageKey: string, fallback: Theme): Theme {
  if (typeof window === "undefined") return fallback
  try {
    const raw = localStorage.getItem(storageKey) as StoredTheme | null
    if (raw === LEGACY_GLASS_THEME) {
      localStorage.setItem(storageKey, "glass")
      return "glass"
    }
    const normalized = normalizeStoredTheme(raw)
    if (normalized) {
      return normalized
    }
  } catch (error) {
    logger.warn('Failed to read theme from localStorage:', error)
  }
  return fallback
}

export function syncDocumentBasicTheme(theme: Theme, enableSystem: boolean, _storageKey: string): void {
  const root = window.document.documentElement
  const lockedToLight = root.classList.contains(SELF_ASSESSMENT_PAGE_CLASS)
  const effectiveTheme: Theme = lockedToLight ? "light" : theme
  const followSystem = lockedToLight ? false : enableSystem
  const mqDark = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false

  DOM_BASIC_THEME_CLASSES.forEach((cls) => root.classList.remove(cls))

  if (effectiveTheme === 'system' && followSystem) {
    // Always follow prefers-color-scheme. Do not retain an SSR/default `.light`
    // class when the OS preference is dark — that broke system-dark smoke/layout.
    root.classList.add(mqDark ? 'dark' : 'light')
    applyShellCanvasToDocument()
    return
  }

  root.classList.add(effectiveTheme === "system" ? "light" : effectiveTheme)
  applyShellCanvasToDocument()
}

/** Pin the document to the bright theme for the public self-assessment funnel. Returns a restore function. */
export function lockDocumentToSelfAssessmentLight(): () => void {
  const root = window.document.documentElement
  root.classList.add(SELF_ASSESSMENT_PAGE_CLASS)
  syncDocumentBasicTheme("light", false, BASIC_THEME_STORAGE_KEY)
  return () => {
    root.classList.remove(SELF_ASSESSMENT_PAGE_CLASS)
    const stored = readStoredBasicTheme(BASIC_THEME_STORAGE_KEY, "light")
    syncDocumentBasicTheme(stored, true, BASIC_THEME_STORAGE_KEY)
  }
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = BASIC_THEME_STORAGE_KEY,
  attribute = "class",
  enableSystem = true,
  themes: _themesProp,
  ...props
}: ThemeProviderProps) {
  /**
   * Must match SSR default so hydration does not mismatch.
   */
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme)
  /** First layout pass must sync from storage, not stale `theme` (App default is `"light"`). Otherwise we briefly force the wrong shell on <html> and CSS vars drift until the next tick. */
  const hasHydratedFromStorageRef = useRef(false)

  useLayoutEffect(() => {
    if (typeof window === "undefined") return

    let effectiveTheme = theme as Theme

    if (!hasHydratedFromStorageRef.current) {
      hasHydratedFromStorageRef.current = true
      const stored = readStoredBasicTheme(storageKey, defaultTheme)
      if (stored !== theme) {
        setThemeState(stored)
        effectiveTheme = stored
      }
    }

    syncDocumentBasicTheme(effectiveTheme, enableSystem, storageKey)
  }, [theme, enableSystem, storageKey, defaultTheme])

  const setTheme = (newTheme: Theme) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, newTheme)
      } catch (error) {
        logger.warn('Failed to save theme to localStorage:', error)
      }
    }
    setThemeState(newTheme)
  }

  const value = {
    theme,
    setTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
